import { prettyDateTime } from "@app/date";
import {
  type AppDeploymentType,
  cancelAppOpsPoll,
  computeCostId,
  createReadableStatus,
  fetchApp,
  fetchCostsByApps,
  fetchEndpointsByAppId,
  fetchImageById,
  fetchServicesByAppId,
  pollAppOperations,
  selectAppById,
  selectAppDeploymentType,
  selectEndpointsByAppId,
  selectEnvironmentById,
  selectImageById,
  selectServicesByAppId,
  selectUserHasPerms,
} from "@app/deploy";
import { fetchDeploymentById, selectDeploymentById } from "@app/deployment";
import { findLoaderComposite } from "@app/loaders";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useCompositeLoader,
  useDispatch,
  useLoader,
  useQuery,
  useSelector,
} from "@app/react";
import {
  appActivityUrl,
  appCiCdUrl,
  appConfigUrl,
  appDeployGetStartedUrl,
  appDetailDeploymentsUrl,
  appDetailDepsUrl,
  appDetailUrl,
  appDisksPathUrl,
  appEndpointsUrl,
  appServicesUrl,
  appSettingsUrl,
  environmentAppsUrl,
  operationDetailUrl,
  sourceDetailUrl,
} from "@app/routes";
import { schema } from "@app/schema";
import { setResourceStats } from "@app/search";
import { fetchSourceById, selectSourceById } from "@app/source";
import type { DeployApp, OperationStatus } from "@app/types";
import { useEffect, useMemo } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import { usePoller } from "../hooks";
import {
  ActiveOperationNotice,
  Banner,
  Box,
  COST_ESTIMATE_TOOLTIP_TEXT,
  CRUMB_VARIANTS,
  DetailLabel,
  DetailPageHeaderView,
  DockerImage,
  GitCommitMessage,
  GitRef,
  IconCheck,
  IconX,
  LastDeployAtDisplay,
  LoadingSpinner,
  Pill,
  ResourceDetailBar,
  type TabItem,
  statusToVariant,
  tokens,
} from "../shared";
import { RollbackButton } from "../shared/deployment/rollback-button";
import { AppSidebarLayout } from "./app-sidebar-layout";
function DeploymentStatusPill({ status }: { status: OperationStatus }) {
  const icon =
    status === "succeeded" ? (
      <IconCheck variant="sm" />
    ) : status === "failed" || status === "canceled" ? (
      <IconX variant="sm" />
    ) : null;
  return (
    <Pill variant={statusToVariant(status)} icon={icon}>
      {createReadableStatus(status)}
    </Pill>
  );
}

function LastDeploymentField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <DetailLabel as="h4" label={label} />
      <div className={`${tokens.type["detail value"]} break-words`}>
        {children}
      </div>
    </div>
  );
}

const deploymentTypeLabels: Record<AppDeploymentType, string> = {
  "git-based": "Git Push",
  "image-based": "Direct Docker Image",
  "companion-repo": "Companion Repo",
  unknown: "Unknown",
};

// Custom widget, not the generic ResourceDetailsTable — this box has its own
// title/action-link header row and a Rollback button, so it doesn't fit the
// reusable "Details" grid shape.
function LastDeploymentWidget({ app }: { app: DeployApp }) {
  const deploymentAction = fetchDeploymentById({
    id: app.currentDeploymentId,
  });
  useQuery(deploymentAction);
  const deployment = useSelector((s) =>
    selectDeploymentById(s, { id: app.currentDeploymentId }),
  );
  const deploymentType = useSelector((s) =>
    selectAppDeploymentType(s, { id: app.id }),
  );

  const imageAction = fetchImageById({ id: deployment.imageId });
  useQuery(imageAction);
  const image = useSelector((s) =>
    selectImageById(s, { id: deployment.imageId }),
  );

  const sourceAction = fetchSourceById({ id: deployment.sourceId });
  useQuery(sourceAction);
  const source = useSelector((s) =>
    selectSourceById(s, { id: deployment.sourceId }),
  );

  const loader = useCompositeLoader([
    deploymentAction,
    imageAction,
    sourceAction,
  ]);

  // Wait for the deployment to load AND, once we know the image/source ids
  // from it, for those dependent fetches to resolve too — otherwise a fresh
  // load briefly renders with a blank digest/source before they arrive.
  const isLoading =
    loader.isLoading ||
    loader.isInitialLoading ||
    deployment.id === "" ||
    (deployment.imageId !== "" && image.id === "") ||
    (deployment.sourceId !== "" && source.id === "");

  if (isLoading) {
    return (
      <Box size="md" className="flex items-center justify-center py-8">
        <LoadingSpinner />
      </Box>
    );
  }

  const deploymentTypeLabel = deploymentTypeLabels[deploymentType];

  return (
    <Box size="md" className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-gray-900">
            Last Deployment
          </h3>
          <Link
            to={operationDetailUrl(deployment.operationId)}
            className="text-sm text-indigo hover:underline"
          >
            View Operation
          </Link>
        </div>
        <Link
          to={appActivityUrl(app.id)}
          className="text-sm text-indigo hover:underline"
        >
          See all activity for this app →
        </Link>
      </div>

      <hr className="border-black-100" />

      <div className="flex items-start gap-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-w-0">
          <LastDeploymentField label="ID">{deployment.id}</LastDeploymentField>
          <LastDeploymentField label="Deployment Type">
            {deploymentTypeLabel}
          </LastDeploymentField>
          <LastDeploymentField label="Git Ref">
            <GitRef
              gitRef={deployment.gitRef}
              commitSha={deployment.gitCommitSha}
              commitUrl={deployment.gitCommitUrl}
            />
          </LastDeploymentField>
          <LastDeploymentField label="Status">
            <DeploymentStatusPill status={deployment.status} />
          </LastDeploymentField>
          <LastDeploymentField label="Docker Image">
            <DockerImage
              image={deployment.dockerImage}
              digest={image.dockerRef}
              repoUrl={deployment.dockerRepositoryUrl}
            />
          </LastDeploymentField>
          <LastDeploymentField label="Timestamp">
            <LastDeployAtDisplay at={deployment.createdAt} />
          </LastDeploymentField>
          <LastDeploymentField label="Commit Message">
            <GitCommitMessage message={deployment.gitCommitMessage} />
          </LastDeploymentField>
          <LastDeploymentField label="Source">
            <Link to={sourceDetailUrl(source.id)}>{source.displayName}</Link>
          </LastDeploymentField>
        </div>

        <RollbackButton app={app} />
      </div>
    </Box>
  );
}

export function AppHeader({
  app,
  isLoading,
}: { app: DeployApp; isLoading: boolean }) {
  const needsFirstDeploy =
    app.id !== "" && !app.deletedAt && !app.currentDeploymentId;
  const getStartedHref = appDeployGetStartedUrl(app.id);

  const orgId = useSelector(selectOrganizationSelectedId);
  useQuery(fetchCostsByApps({ orgId }));
  const { isLoading: isCostLoading } = useLoader(fetchCostsByApps);
  const cost = useSelector((s) =>
    schema.costs.selectById(s, { id: computeCostId("App", app.id) }),
  );

  return (
    <div className="flex flex-col gap-4">
      {!!app.deletedAt && (
        <Banner className="mt-2 mb-4" variant="warning">
          This app was deprovisioned on {prettyDateTime(app.deletedAt)}.
        </Banner>
      )}
      {needsFirstDeploy ? (
        <Banner variant="warning" className="mb-4">
          <span className={tokens.type.darker}>
            This app hasn&apos;t been deployed yet.{" "}
            <Link to={getStartedHref} className="underline">
              Resume deployment
            </Link>
          </span>
        </Banner>
      ) : null}
      <ResourceDetailBar
        isLoading={isLoading}
        icon={
          <img
            src="/resource-types/logo-app.png"
            className="w-[32px] h-[32px]"
            aria-label="App"
          />
        }
        nameLabel="App"
        name={app.handle}
        id={app.id}
        createdAt={app.createdAt}
        cost={isCostLoading || cost.id === "" ? null : cost.estCost}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />
      {app.currentDeploymentId ? <LastDeploymentWidget app={app} /> : null}
    </div>
  );
}

const AppHeartbeatNotice = ({ id }: { id: string }) => {
  const poller = useMemo(() => pollAppOperations({ id }), [id]);
  const cancel = useMemo(() => cancelAppOpsPoll(), []);

  usePoller({
    action: poller,
    cancel,
  });

  return <ActiveOperationNotice resourceId={id} resourceType="app" />;
};

function AppPageHeader() {
  const { id = "" } = useParams();
  const dispatch = useDispatch();

  const loaderApp = useQuery(
    fetchApp({ id, withDeleted: true, waitForBootup: true }),
  );
  const loaderServices = useQuery(
    fetchServicesByAppId({ id: id, withDeleted: true, waitForBootup: true }),
  );
  const app = useSelector((s) => selectAppById(s, { id }));
  const environment = useSelector((s) =>
    selectEnvironmentById(s, { id: app.environmentId }),
  );
  const loaderEndpoints = useQuery(fetchEndpointsByAppId({ appId: id }));
  const services = useSelector((s) => selectServicesByAppId(s, { appId: id }));
  const endpoints = useSelector((s) =>
    selectEndpointsByAppId(s, { appId: id }),
  );
  const loader = findLoaderComposite([
    loaderApp,
    loaderServices,
    loaderEndpoints,
  ]);

  useEffect(() => {
    dispatch(setResourceStats({ id, type: "app", deletedAt: app?.deletedAt }));
  }, [app?.deletedAt]);

  const crumbs = [];
  if (environment.id) {
    crumbs.push({
      name: environment.handle,
      to: environmentAppsUrl(environment.id),
      label: "Environment",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    });
  } else {
    crumbs.push({
      name: "Unknown Environment",
      to: "#",
      label: "Environment",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    });
  }

  const hasConfigAccess = useSelector((s) =>
    selectUserHasPerms(s, { envId: app.environmentId, scope: "read" }),
  );

  const tabs: TabItem[] = [];
  if (!app.deletedAt)
    tabs.push(
      ...[
        { name: "Deployments", href: appDetailDeploymentsUrl(id) },
        {
          name: "Services",
          href: appServicesUrl(id),
          count: loader.isInitialLoading ? null : services.length,
        },
        {
          name: "Endpoints",
          href: appEndpointsUrl(id),
          count: loader.isInitialLoading ? null : endpoints.length,
        },
        { name: "Disk Storage", href: appDisksPathUrl(id) },
      ],
    );

  tabs.push({ name: "Activity", href: appActivityUrl(id) });

  if (!app.deletedAt) {
    tabs.push({ name: "Configuration", href: appConfigUrl(id) });
    if (hasConfigAccess) {
      tabs.push({ name: "Dependencies", href: appDetailDepsUrl(id) });
    }

    tabs.push(
      { name: "CI/CD", href: appCiCdUrl(id) },
      { name: "Settings", href: appSettingsUrl(id) },
    );
  }

  return (
    <>
      {app.id && !app.deletedAt ? <AppHeartbeatNotice id={id} /> : null}
      <DetailPageHeaderView
        {...loader}
        breadcrumbs={crumbs}
        titleLabel="App"
        title={app.handle}
        detailsBox={<AppHeader app={app} isLoading={loader.isLoading} />}
        tabs={tabs}
        lastBreadcrumbTo={appDetailUrl(app.id)}
      />
    </>
  );
}

export const AppDetailLayout = () => {
  return (
    <AppSidebarLayout
      header={<AppPageHeader />}
      mainWrapperDivClassName="overflow-x-hidden"
    >
      <Outlet />
    </AppSidebarLayout>
  );
};
